import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import PageTitle from '../components/PageTitle';

const STEPS = ['Business Info', 'Owner Details', 'Bank & Address', 'Documents', 'Review & Submit'];

const INIT = {
  businessName: '', businessType: '', businessCategory: '', turnover: '',
  pan: '', email: '', phone: '', ownerName: '', aadhar: '',
  businessAddress: '', state: '', pincode: '',
  bankName: '', accountNo: '', ifsc: '', remarks: '',
};

const BUSINESS_TYPES = ['Proprietorship', 'Partnership', 'Private Limited', 'LLP', 'OPC', 'HUF', 'Trust/Society'];
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh'];

const GST_STEPS_TRACKER = [
  { key: 'SUBMITTED',           label: 'Submitted',         desc: 'Application received by our CA team.' },
  { key: 'DOCUMENTS_VERIFIED',  label: 'Docs Verified',     desc: 'Your documents have been verified.' },
  { key: 'APPLICATION_FILED',   label: 'Filed on Portal',   desc: 'Application filed on GST portal, ARN generated.' },
  { key: 'GSTIN_ASSIGNED',      label: 'GSTIN Assigned',    desc: 'Your GSTIN has been assigned. Download certificate.' },
];

function Field({ label, name, type = 'text', placeholder, required, span2, value, onChange, error, hint, children }) {
  return (
    <div className={`field${span2 ? ' span2' : ''}`}>
      <label>{label}{required && <span className="req">*</span>}</label>
      {children || <input name={name} type={type} placeholder={placeholder} value={value} onChange={onChange} className={error ? 'err' : ''} />}
      {hint && <span className="hint">{hint}</span>}
      {error && <span className="err-msg">⚠ {error}</span>}
    </div>
  );
}

function FileZone({ label, name, hint, file, onChange }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className={`file-zone ${file ? 'has-file' : ''}`}>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => onChange(name, e.target.files[0])} />
        <div style={{ fontSize: '1.4rem', marginBottom: '.2rem' }}>{file ? '✅' : '📎'}</div>
        <div className="file-zone-label">{file ? 'Selected' : 'Click to upload'}</div>
        {file ? <div className="file-name">📄 {file.name}</div> : <div className="file-zone-sub">{hint}</div>}
      </div>
    </div>
  );
}

export default function GstRegistration() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep]       = useState(1);
  const [data, setData]       = useState({
    ...INIT,
    ownerName: user?.fullName || '',
    email:     user?.email    || '',
    phone:     user?.phone    || '',
    pan:       user?.pan      || '',
  });
  const [files, setFiles]     = useState({});
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(null);

  const handle = e => {
    const { name, value } = e.target;
    const v = ['pan', 'ifsc'].includes(name) ? value.toUpperCase() : value;
    setData(d => ({ ...d, [name]: v }));
  };

  const handleFile = (name, file) => {
    if (file && file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setFiles(f => ({ ...f, [name]: file }));
  };

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!data.businessName.trim()) e.businessName = 'Required';
      if (!data.businessType)        e.businessType  = 'Required';
      if (!data.pan.trim() || !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(data.pan)) e.pan = 'Invalid PAN';
    }
    if (step === 2) {
      if (!data.ownerName.trim()) e.ownerName = 'Required';
      if (!data.email.trim() || !/^[\w.+-]+@[\w-]+\.[a-z]{2,}$/i.test(data.email)) e.email = 'Invalid email';
      if (!data.phone.trim() || !/^[6-9]\d{9}$/.test(data.phone)) e.phone = 'Invalid mobile';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    setLoading(true); setApiError('');
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const { data: res } = await api.post('/api/gst/submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res);
    } catch (err) {
      setApiError(err.response?.data?.error || 'Submission failed');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <>
        <PageTitle title="GST Application Submitted" />
        <div className="page" style={{ maxWidth: 600 }}>
          <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ width: 80, height: 80, background: 'var(--greenl)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✅</div>
            <h2 style={{ color: 'var(--navy)', marginBottom: '.5rem' }}>GST Application Submitted!</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>Application #{success.id} received by our CA team.</p>
            <div style={{ background: 'var(--bluel)', color: 'var(--blue)', padding: '.5rem 1.5rem', borderRadius: 8, fontWeight: 700, display: 'inline-block', marginBottom: '1.5rem' }}>
              Business: {success.businessName}
            </div>
            <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '1.25rem', textAlign: 'left', marginBottom: '2rem' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '.75rem' }}>What happens next</div>
              {['CA verifies your documents (1-2 days)', 'Application filed on GST portal', 'ARN number sent to your email', 'GSTIN assigned in 3-7 working days', 'GST certificate available for download'].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '.75rem', marginBottom: '.5rem', fontSize: '.85rem', color: 'var(--muted)', alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, background: 'var(--navy)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                  {s}
                </div>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/applications')}>Track Application →</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title="GST Registration" />
      <div className="page-hero">
        <div className="page-hero-label">GST Registration</div>
        <h1>Register Your Business for GST</h1>
        <p>GSTIN in 3-7 working days — complete document support included</p>
      </div>

      {/* Progress */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div className="progress-track">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'active' : 'pending';
              return (
                <div key={label} className={`pt-step ${state}`}>
                  <div className="pt-circle">{state === 'done' ? '✓' : n}</div>
                  <div className="pt-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="page">
        <div className="card">
          {apiError && <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>⚠ {apiError}</div>}

          {/* Step 1 — Business Info */}
          {step === 1 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 1 of 5</div>
                <h2 className="step-title">Business Information</h2>
                <p className="step-desc">Basic details about your business entity.</p>
              </div>
              <div className="form-grid">
                <Field span2 name="businessName" label="Business / Trade Name" required placeholder="e.g. Sharma Enterprises" value={data.businessName} onChange={handle} error={errors.businessName} />
                <div className="field">
                  <label>Business Type<span className="req">*</span></label>
                  <select name="businessType" value={data.businessType} onChange={handle} className={errors.businessType ? 'err' : ''}>
                    <option value="">Select type...</option>
                    {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.businessType && <span className="err-msg">⚠ {errors.businessType}</span>}
                </div>
                <div className="field">
                  <label>Nature of Business</label>
                  <select name="businessCategory" value={data.businessCategory} onChange={handle}>
                    <option value="">Select...</option>
                    <option value="Goods">Goods (Products)</option>
                    <option value="Services">Services</option>
                    <option value="Both">Both Goods & Services</option>
                  </select>
                </div>
                <Field name="pan" label="PAN of Business / Owner" required placeholder="ABCDE1234F" maxLength={10} value={data.pan} onChange={handle} error={errors.pan} hint="GST registration is linked to PAN" />
                <div className="field">
                  <label>Annual Turnover</label>
                  <select name="turnover" value={data.turnover} onChange={handle}>
                    <option value="">Select range...</option>
                    <option value="Below 20 Lakhs">Below ₹20 Lakhs</option>
                    <option value="20-40 Lakhs">₹20 – ₹40 Lakhs</option>
                    <option value="40 Lakhs - 1 Crore">₹40 Lakhs – ₹1 Crore</option>
                    <option value="Above 1 Crore">Above ₹1 Crore</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Step 2 — Owner Details */}
          {step === 2 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 2 of 5</div>
                <h2 className="step-title">Owner / Proprietor Details</h2>
              </div>
              <div className="form-grid">
                <Field name="ownerName" label="Owner Full Name" required placeholder="Rahul Kumar Sharma" value={data.ownerName} onChange={handle} error={errors.ownerName} />
                <Field name="aadhar" label="Aadhar Number" placeholder="1234 5678 9012" maxLength={14} value={data.aadhar} onChange={handle} />
                <Field name="email" label="Email Address" type="email" required placeholder="you@email.com" value={data.email} onChange={handle} error={errors.email} />
                <Field name="phone" label="Mobile Number" required placeholder="9876543210" maxLength={10} value={data.phone} onChange={handle} error={errors.phone} />
              </div>
            </>
          )}

          {/* Step 3 — Bank & Address */}
          {step === 3 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 3 of 5</div>
                <h2 className="step-title">Bank & Business Address</h2>
              </div>
              <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--navy)', marginBottom: '1rem' }}>🏦 Bank Details</div>
              <div className="form-grid three" style={{ marginBottom: '1.75rem' }}>
                <Field name="bankName" label="Bank Name" placeholder="State Bank of India" value={data.bankName} onChange={handle} />
                <Field name="accountNo" label="Account Number" placeholder="00000012345678" value={data.accountNo} onChange={handle} />
                <Field name="ifsc" label="IFSC Code" placeholder="SBIN0001234" maxLength={11} value={data.ifsc} onChange={handle} />
              </div>
              <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--navy)', marginBottom: '1rem' }}>📍 Business Address</div>
              <div className="form-grid">
                <Field span2 name="businessAddress" label="Full Address" placeholder="Shop/Office No, Street, Area" value={data.businessAddress} onChange={handle} />
                <div className="field">
                  <label>State</label>
                  <select name="state" value={data.state} onChange={handle}>
                    <option value="">Select state...</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Field name="pincode" label="PIN Code" placeholder="302001" maxLength={6} value={data.pincode} onChange={handle} />
              </div>
            </>
          )}

          {/* Step 4 — Documents */}
          {step === 4 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 4 of 5</div>
                <h2 className="step-title">Upload Documents</h2>
                <p className="step-desc">All documents should be clear and readable. PDF, JPG, PNG — max 5MB each.</p>
              </div>
              <div className="alert alert-info" style={{ marginBottom: '1.5rem', fontSize: '.85rem' }}>
                📋 Required: PAN + Aadhar + Address Proof + Bank Statement. Business proof required for companies/LLPs.
              </div>
              <div className="form-grid">
                <FileZone name="docPan" label="PAN Card" hint="Owner/Business PAN card copy" file={files.docPan} onChange={handleFile} />
                <FileZone name="docAadhar" label="Aadhar Card" hint="Owner Aadhar card (front & back)" file={files.docAadhar} onChange={handleFile} />
                <FileZone name="docAddressProof" label="Business Address Proof" hint="Electricity bill, rent agreement, or ownership proof" file={files.docAddressProof} onChange={handleFile} />
                <FileZone name="docBankStatement" label="Bank Statement / Cancelled Cheque" hint="Last 3 months statement or cancelled cheque" file={files.docBankStatement} onChange={handleFile} />
                <FileZone name="docBusinessProof" label="Business Constitution Proof" hint="MOA/AOA for company, Partnership deed, etc." file={files.docBusinessProof} onChange={handleFile} />
                <FileZone name="docOther" label="Other Documents" hint="Any other supporting documents" file={files.docOther} onChange={handleFile} />
              </div>
              <div className="field" style={{ marginTop: '1rem' }}>
                <label>Additional Remarks</label>
                <textarea name="remarks" rows={3} placeholder="Any special notes for our CA team..." value={data.remarks} onChange={handle} />
              </div>
            </>
          )}

          {/* Step 5 — Review */}
          {step === 5 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 5 of 5</div>
                <h2 className="step-title">Review & Submit</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Business Info</div>
                  {[['Business Name', data.businessName], ['Type', data.businessType], ['PAN', data.pan], ['Turnover', data.turnover]].map(([l, v]) => v ? (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.85rem' }}>
                      <span style={{ color: 'var(--muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ) : null)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Owner & Contact</div>
                  {[['Owner', data.ownerName], ['Email', data.email], ['Phone', data.phone], ['State', data.state], ['Bank', data.bankName]].map(([l, v]) => v ? (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.85rem' }}>
                      <span style={{ color: 'var(--muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ) : null)}
                </div>
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Documents</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                  {Object.entries(files).filter(([, v]) => v).map(([k, v]) => (
                    <span key={k} className="badge badge-filed">📄 {v.name}</span>
                  ))}
                  {Object.keys(files).length === 0 && <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>No documents uploaded</span>}
                </div>
              </div>
              <div className="alert alert-info" style={{ marginTop: '1.5rem', fontSize: '.83rem' }}>
                ⚠️ By submitting, you authorise MyTaxNiti to file your GST registration application with the GST Portal on your behalf.
              </div>
            </>
          )}

          <div className="btn-row">
            {step > 1 ? <button className="btn btn-outline" onClick={prev}>← Back</button> : <span />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Step {step} of 5</span>
              {step < 5
                ? <button className="btn btn-primary" onClick={next}>Continue →</button>
                : <button className="btn btn-gold" onClick={submit} disabled={loading}>
                    {loading ? <><span className="spinner" /> Submitting...</> : '🚀 Submit Application'}
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
}